import {graphql} from 'gatsby'

import {Grid, Flex} from 'src/components/Box'
import Card, {CardProps} from 'src/components/Card'
import {Header4, SmallBody} from 'src/components/text'
import {ActionLink} from 'src/components/Action'
import {Maybe} from 'src/graphql-generated'

interface Props {
  link: string
  title: string
  description: string
  heroUrl?: Maybe<CardProps['backgroundImage']>
}

export const query = graphql`
  fragment ArticleCard on ContentfulJourneyTile {
    __typename
    id
    permalink
    tile {
      id
      title
      summary {
        summary
      }
      updatedAt
      heroRemoteUrl
      heroFile {
        ...CardBackgroundImage
      }
    }
  }
`

const ArticleContent: React.FunctionComponent<
  Pick<Props, 'title' | 'description'>
> = ({title, description}) => (
  <Grid gridGap="2">
    <Header4>{title}</Header4>
    <SmallBody color="grey5" lines={2}>
      {description}
    </SmallBody>
  </Grid>
)

const ArticleCard: React.FunctionComponent<Omit<Props, 'link'>> = ({
  title,
  description,
  heroUrl,
}) => {
  if (!heroUrl) {
    return (
      <Card>
        <ArticleContent title={title} description={description} />
      </Card>
    )
  }

  return (
    <Card backgroundImage={heroUrl}>
      <Flex
        borderBottomLeftRadius={{_: '19px', lg: 0}}
        borderTopRightRadius={{_: 0, lg: '19px'}}
        borderBottomRightRadius="19px"
        p="4"
        mt={{_: '85px', lg: -4}}
        ml={{lg: '6.5rem'}}
        mx={-4}
        mb={-4}
        bg="white"
        width={{_: '100%', lg: 'initial'}}
        height="100%"
      >
        <ArticleContent title={title} description={description} />
      </Flex>
    </Card>
  )
}

const Article = ({title, link, description, heroUrl}: Props) => {
  // Check if the link is external for scholarships
  const isExternalLink = link.startsWith('http') || link.startsWith('https')

  return (
    <>
      {isExternalLink ? (
        <a
          data-cy="journey-article"
          href={link} // External link, no base URL appending
          target="_blank"
          rel="noopener noreferrer"
          style={{height: '100%', display: 'block'}}
        >
          <ArticleCard
            title={title}
            description={description}
            heroUrl={heroUrl}
          />
        </a>
      ) : (
        <ActionLink
          data-cy="journey-article"
          to={link} // Internal link, append base URL
          css={{height: '100%'}}
        >
          <ArticleCard
            title={title}
            description={description}
            heroUrl={heroUrl}
          />
        </ActionLink>
      )}
    </>
  )
}

export default Article
